/**
 * 3D ductwork for the Viewer: extruded geometry along duct centerlines —
 * round ducts as cylinder segments per straight run, rect ducts as boxes —
 * plus small distinctive fitting markers (elbow/tee/reducer/boot/damper) and
 * terminal markers (supply diffuser = flat square, return grille = lined
 * square). Supply vs return are color-coded (see ../mep/ducts DUCT_COLORS).
 *
 * Performance choice (documented per task contract): duct runs are merged
 * into ONE BufferGeometry per system per storey (`ducts:supply:<storey>` /
 * `ducts:return:<storey>`), so a whole building renders with a handful of
 * draw calls instead of one mesh per segment. Fittings and terminals are
 * InstancedMesh per kind. Smooth elbow joins are out of scope this wave.
 *
 * Owns a THREE.Group the Viewer adds to its scene — pure three.js object
 * management, no renderer needed, unit-testable in jsdom.
 *
 * Coordinates: server data is plan meters (x east, y north, z up); three.js
 * is y-up, so everything maps through (x, z, y) — same as DeviceLayer and
 * the Fragments building mesh; the per-model offset is applied on `group`.
 */
import * as THREE from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import {
  DUCT_COLORS,
  fittingColor,
  planToThree,
  segmentFrame,
  segmentTransforms,
  terminalSystem,
  type Vec3,
} from "../mep/ducts";
import type { DuctShape, DuctSystem, EquipmentKind, MepEquipment } from "../api/types";

/** What the layer needs per duct: the DTO plus the derived storey id. */
export interface LayerDuct {
  id: string;
  system: DuctSystem;
  centerline: Vec3[];
  shape: DuctShape;
  diameter_m: number | null;
  width_m: number | null;
  height_m: number | null;
  /** Derived via assignDuctStoreys (the duct DTO has no storey id). */
  storey_id: string | null;
}

export interface LayerFitting {
  id: string;
  kind: string;
  x_m: number;
  y_m: number;
  z_m: number;
  /** Derived via assignFittingStoreys. */
  storey_id: string | null;
}

export interface LayerTerminal {
  id: string;
  kind: string;
  x_m: number;
  y_m: number;
  z_m: number;
  storey_id: string | null;
}

/** What the layer needs per equipment row (mirrors the {@link MepEquipment} DTO). */
export interface LayerEquipment {
  id: string;
  kind: EquipmentKind;
  x_m: number;
  y_m: number;
  z_m: number;
  /** True for placeholder/screening-only rows; visualized with a softer material. */
  screening_only: boolean;
  /** Server-side storey id for storey-isolation. */
  storey_id: string | null;
}

/** Fallback cross-section when the server omits a size (meters). */
const DEFAULT_SIZE = 0.15;
/** Fitting marker radius (meters). */
const FITTING_RADIUS = 0.18;
/** Terminal face half-size (meters) — a 0.6 m square face. */
const TERMINAL_HALF = 0.3;
/**
 * Equipment glyph footprint (meters) — matches the `generic-ahu-placeholder`
 * catalog seed (2.5 m × 1.5 m × 1.0 m). One distinctive box per AHU drawn at
 * the trunk centerline z, so the operator can see "the AHU is right here".
 */
const EQUIPMENT_SIZE: Record<EquipmentKind, [number, number, number]> = {
  air_handling_unit: [2.5, 1.5, 1.0],
};
/**
 * Glyph fill color per equipment kind — picked to contrast with the supply
 * (blue) / return (red) duct colors in {@link DUCT_COLORS}.
 */
const EQUIPMENT_COLOR: Record<EquipmentKind, number> = {
  air_handling_unit: 0xf2a93b, // amber/orange
};

/**
 * Pure helper: shape, label, and footprint for one equipment row. Lives next
 * to the layer so the page can render an HTML test/debug hook from the same
 * spec the Three.js mesh uses (size matches the catalog seed footprint;
 * `(placeholder)` suffix calls out the screening-only stub).
 */
export interface EquipmentMeshSpec {
  label: string;
  center: [number, number, number];
  size: [number, number, number];
  screeningOnly: boolean;
}

export function equipmentMeshSpec(equipment: LayerEquipment | MepEquipment): EquipmentMeshSpec {
  const kindLabel = equipment.kind === "air_handling_unit" ? "AHU" : equipment.kind;
  const suffix = equipment.screening_only ? " (placeholder)" : "";
  return {
    label: `${kindLabel}${suffix} — ${equipment.id}`,
    center: [equipment.x_m, equipment.y_m, equipment.z_m],
    size: EQUIPMENT_SIZE[equipment.kind] ?? [1.0, 1.0, 1.0],
    screeningOnly: equipment.screening_only,
  };
}

function ductSegmentGeometry(duct: LayerDuct): THREE.BufferGeometry[] {
  const geometries: THREE.BufferGeometry[] = [];
  for (const segment of segmentTransforms(duct.centerline)) {
    const forward = planToThree(segment.dir);
    const { right, up } = segmentFrame(forward);
    let geometry: THREE.BufferGeometry;
    if (duct.shape === "rect") {
      const w = duct.width_m ?? DEFAULT_SIZE;
      const h = duct.height_m ?? DEFAULT_SIZE;
      // Box local x = width (kept horizontal), y = height, z = run length.
      geometry = new THREE.BoxGeometry(w, h, segment.length);
    } else {
      const r = (duct.diameter_m ?? DEFAULT_SIZE) / 2;
      // Cylinder axis is local +Y; re-axis to +Z so the shared frame applies.
      geometry = new THREE.CylinderGeometry(r, r, segment.length, 12, 1);
      geometry.rotateX(Math.PI / 2);
    }
    const basis = new THREE.Matrix4().makeBasis(
      new THREE.Vector3(...right),
      new THREE.Vector3(...up),
      new THREE.Vector3(...forward),
    );
    basis.setPosition(new THREE.Vector3(...planToThree(segment.mid)));
    geometry.applyMatrix4(basis);
    geometries.push(geometry);
  }
  return geometries;
}

function fittingGeometry(kind: string): THREE.BufferGeometry {
  switch (kind) {
    case "elbow":
      return new THREE.SphereGeometry(FITTING_RADIUS, 10, 8);
    case "tee":
      return new THREE.ConeGeometry(FITTING_RADIUS, FITTING_RADIUS * 2, 4);
    case "reducer":
      return new THREE.CylinderGeometry(FITTING_RADIUS * 0.5, FITTING_RADIUS, FITTING_RADIUS * 2, 10);
    case "boot":
      return new THREE.BoxGeometry(FITTING_RADIUS * 1.5, FITTING_RADIUS * 1.5, FITTING_RADIUS * 1.5);
    // Dampers (fire/smoke/combination) share the octahedron; color separates them.
    default:
      return new THREE.OctahedronGeometry(FITTING_RADIUS * 1.2);
  }
}

/** Flat square face (supply diffuser). */
function diffuserGeometry(): THREE.BufferGeometry {
  return new THREE.BoxGeometry(TERMINAL_HALF * 2, 0.04, TERMINAL_HALF * 2);
}

/** Lined square (return grille): flat face merged with three raised bars. */
function grilleGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [
    new THREE.BoxGeometry(TERMINAL_HALF * 2, 0.03, TERMINAL_HALF * 2),
  ];
  for (const offset of [-0.5, 0, 0.5]) {
    const bar = new THREE.BoxGeometry(TERMINAL_HALF * 2 * 0.9, 0.06, 0.05);
    bar.translate(0, 0, offset * TERMINAL_HALF);
    parts.push(bar);
  }
  const merged = mergeGeometries(parts, false);
  for (const part of parts) part.dispose();
  return merged ?? new THREE.BoxGeometry(TERMINAL_HALF * 2, 0.03, TERMINAL_HALF * 2);
}

export class DuctLayer {
  /** Added to the Viewer's scene; everything the layer renders lives here. */
  readonly group = new THREE.Group();

  private ducts: LayerDuct[] = [];
  private fittings: LayerFitting[] = [];
  private terminals: LayerTerminal[] = [];
  private equipment: LayerEquipment[] = [];
  private storeyFilter: string | null = null;
  private built: (THREE.Mesh | THREE.InstancedMesh)[] = [];

  // Shared resources reused across rebuilds (storey filter / visibility
  // toggles): fitting/terminal/equipment geometries by kind, and materials by
  // color-key. Created lazily; disposed only in dispose(), never in clear() —
  // clear() must not dispose anything held here (see clear()). Duct meshes use
  // per-rebuild merged geometry (filter-dependent) that is NOT cached and IS
  // disposed by clear().
  private geoCache = new Map<string, THREE.BufferGeometry>();
  private matCache = new Map<string, THREE.MeshStandardMaterial>();

  constructor() {
    this.group.name = "istari-duct-layer";
  }

  private cachedGeometry(
    key: string,
    make: () => THREE.BufferGeometry,
  ): THREE.BufferGeometry {
    let geo = this.geoCache.get(key);
    if (!geo) {
      geo = make();
      this.geoCache.set(key, geo);
    }
    return geo;
  }

  private cachedMaterial(
    key: string,
    make: () => THREE.MeshStandardMaterial,
  ): THREE.MeshStandardMaterial {
    let mat = this.matCache.get(key);
    if (!mat) {
      mat = make();
      this.matCache.set(key, mat);
    }
    return mat;
  }

  /** Replace all ductwork (called after generation/regeneration). */
  setDucts(
    ducts: readonly LayerDuct[],
    fittings: readonly LayerFitting[] = [],
    terminals: readonly LayerTerminal[] = [],
    equipment: readonly LayerEquipment[] = [],
  ): void {
    this.ducts = [...ducts];
    this.fittings = [...fittings];
    this.terminals = [...terminals];
    this.equipment = [...equipment];
    this.rebuild();
  }

  /** Toggle the whole ducts layer (independent of fire-device markers). */
  setLayerVisible(visible: boolean): void {
    this.group.visible = visible;
  }

  get layerVisible(): boolean {
    return this.group.visible;
  }

  /**
   * Restrict ductwork to one storey (`null` shows all), matching the server
   * storey ids on terminals / derived for ducts. Ducts and fittings WITHOUT a
   * derived storey stay visible under a filter (no terminals → no inference
   * basis; hiding them would blank the layer), while storey-less terminals
   * hide, matching DeviceLayer's no-fake-placement rule.
   */
  setStoreyFilter(storeyId: string | null): void {
    this.storeyFilter = storeyId;
    this.rebuild();
  }

  /** Names of the meshes currently built, sorted (deterministic; for tests/UI). */
  meshNames(): string[] {
    return this.built.map((m) => m.name).sort();
  }

  /** Rendered instance count for one fitting/terminal mesh name (0 if absent). */
  instanceCount(name: string): number {
    const mesh = this.built.find((m) => m.name === name);
    return mesh instanceof THREE.InstancedMesh ? mesh.count : 0;
  }

  private rebuild(): void {
    this.clear();
    this.buildDucts();
    this.buildFittings();
    this.buildTerminals();
    this.buildEquipment();
  }

  private passesFilter(storeyId: string | null, keepUnassigned: boolean): boolean {
    if (this.storeyFilter === null) return true;
    if (storeyId === null) return keepUnassigned;
    return storeyId === this.storeyFilter;
  }

  /** One merged BufferGeometry per system per storey (see module docs). */
  private buildDucts(): void {
    const groups = new Map<string, THREE.BufferGeometry[]>();
    for (const duct of this.ducts) {
      if (!this.passesFilter(duct.storey_id, true)) continue;
      const key = `ducts:${duct.system}:${duct.storey_id ?? "none"}`;
      const list = groups.get(key) ?? [];
      list.push(...ductSegmentGeometry(duct));
      groups.set(key, list);
    }
    for (const key of [...groups.keys()].sort()) {
      const parts = groups.get(key)!;
      const merged = mergeGeometries(parts, false);
      for (const part of parts) part.dispose();
      if (!merged) continue;
      const system = key.split(":")[1] as DuctSystem;
      const mesh = new THREE.Mesh(
        merged,
        this.cachedMaterial(`duct:${system}`, () =>
          new THREE.MeshStandardMaterial({
            color: new THREE.Color(DUCT_COLORS[system]),
            metalness: 0.3,
            roughness: 0.6,
          }),
        ),
      );
      mesh.name = key;
      this.add(mesh);
    }
  }

  private buildFittings(): void {
    const byKind = new Map<string, LayerFitting[]>();
    for (const fitting of this.fittings) {
      if (!this.passesFilter(fitting.storey_id, true)) continue;
      const list = byKind.get(fitting.kind) ?? [];
      list.push(fitting);
      byKind.set(fitting.kind, list);
    }
    const matrix = new THREE.Matrix4();
    for (const kind of [...byKind.keys()].sort()) {
      const list = byKind.get(kind)!;
      const mesh = new THREE.InstancedMesh(
        this.cachedGeometry(`fitting:${kind}`, () => fittingGeometry(kind)),
        this.cachedMaterial(`fitting:${kind}`, () =>
          new THREE.MeshStandardMaterial({ color: new THREE.Color(fittingColor(kind)) }),
        ),
        list.length,
      );
      mesh.name = `fittings:${kind}`;
      list.forEach((fitting, i) => {
        matrix.makeTranslation(...planToThree([fitting.x_m, fitting.y_m, fitting.z_m]));
        mesh.setMatrixAt(i, matrix);
      });
      mesh.instanceMatrix.needsUpdate = true;
      this.add(mesh);
    }
  }

  private buildTerminals(): void {
    const byKind = new Map<string, LayerTerminal[]>();
    for (const terminal of this.terminals) {
      if (!this.passesFilter(terminal.storey_id, false)) continue;
      const list = byKind.get(terminal.kind) ?? [];
      list.push(terminal);
      byKind.set(terminal.kind, list);
    }
    const matrix = new THREE.Matrix4();
    for (const kind of [...byKind.keys()].sort()) {
      const list = byKind.get(kind)!;
      const system = terminalSystem(kind);
      const mesh = new THREE.InstancedMesh(
        this.cachedGeometry(`terminal:${system}`, () =>
          system === "return" ? grilleGeometry() : diffuserGeometry(),
        ),
        this.cachedMaterial(`terminal:${system}`, () =>
          new THREE.MeshStandardMaterial({ color: new THREE.Color(DUCT_COLORS[system]) }),
        ),
        list.length,
      );
      mesh.name = `terminals:${kind}`;
      list.forEach((terminal, i) => {
        matrix.makeTranslation(...planToThree([terminal.x_m, terminal.y_m, terminal.z_m]));
        mesh.setMatrixAt(i, matrix);
      });
      mesh.instanceMatrix.needsUpdate = true;
      this.add(mesh);
    }
  }

  /**
   * Equipment glyphs (one InstancedMesh per kind). Screening-only AHUs render
   * with a translucent material so the placeholder reads "stub, not real
   * sized equipment"; the operator-facing label suffix lives in the page-side
   * DOM hook, not in the canvas.
   */
  private buildEquipment(): void {
    const byKind = new Map<EquipmentKind, LayerEquipment[]>();
    for (const eq of this.equipment) {
      if (!this.passesFilter(eq.storey_id, true)) continue;
      const list = byKind.get(eq.kind) ?? [];
      list.push(eq);
      byKind.set(eq.kind, list);
    }
    const matrix = new THREE.Matrix4();
    for (const kind of [...byKind.keys()].sort()) {
      const list = byKind.get(kind)!;
      const [w, d, h] = EQUIPMENT_SIZE[kind];
      // Plan footprint is x × y on plan, with h vertical (z up). Map into
      // three.js (y-up) the same way other layers do via planToThree:
      // plan (x, y, z) → three (x, z, y), so a plan box (w × d × h)
      // becomes a three box (w × h × d).
      const geometry = this.cachedGeometry(
        `equipment:${kind}`,
        () => new THREE.BoxGeometry(w, h, d),
      );
      const screeningOnly = list.every((e) => e.screening_only);
      const mesh = new THREE.InstancedMesh(
        geometry,
        this.cachedMaterial(`equipment:${kind}:${screeningOnly}`, () =>
          new THREE.MeshStandardMaterial({
            color: new THREE.Color(EQUIPMENT_COLOR[kind]),
            transparent: screeningOnly,
            opacity: screeningOnly ? 0.55 : 1.0,
            metalness: 0.2,
            roughness: 0.7,
          }),
        ),
        list.length,
      );
      mesh.name = `equipment:${kind}`;
      list.forEach((eq, i) => {
        matrix.makeTranslation(...planToThree([eq.x_m, eq.y_m, eq.z_m]));
        mesh.setMatrixAt(i, matrix);
      });
      mesh.instanceMatrix.needsUpdate = true;
      this.add(mesh);
    }
  }

  private add(mesh: THREE.Mesh | THREE.InstancedMesh): void {
    this.built.push(mesh);
    this.group.add(mesh);
  }

  private clear(): void {
    const cachedGeo = new Set<THREE.BufferGeometry>(this.geoCache.values());
    const cachedMat = new Set<THREE.Material>(this.matCache.values());
    for (const mesh of this.built) {
      this.group.remove(mesh);
      // Only dispose per-mesh resources not held in the shared caches (the
      // merged duct geometry is unique per rebuild; everything else is reused).
      if (!cachedGeo.has(mesh.geometry)) mesh.geometry.dispose();
      const material = mesh.material as THREE.Material;
      if (!cachedMat.has(material)) material.dispose();
      // Frees the InstancedMesh's own instance buffers (not geometry/material).
      if (mesh instanceof THREE.InstancedMesh) mesh.dispose();
    }
    this.built = [];
  }

  dispose(): void {
    this.clear();
    for (const geo of this.geoCache.values()) geo.dispose();
    for (const mat of this.matCache.values()) mat.dispose();
    this.geoCache.clear();
    this.matCache.clear();
    this.ducts = [];
    this.fittings = [];
    this.terminals = [];
    this.equipment = [];
  }

  /** Currently rendered equipment, in input order. Used by the page DOM hook. */
  equipmentList(): readonly LayerEquipment[] {
    return this.equipment;
  }
}
